import React, { useEffect, useState } from 'react';
import { Box, IconButton, Modal, useMediaQuery } from '@mui/material';
import { CloseIconWhite } from '@shared/components/icons/svg-icon';
import {
  FileUploadType,
  MediaModalProps
} from '@shared/models/file-upload.models.ts';
import { getDownloadFileUrl } from '@api/file-upload-api.ts';

const MediaModal: React.FC<MediaModalProps> = ({
  type,
  selectedItem,
  onClose,
  showDialog
}) => {
  const [mediaSrc, setMediaSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isTabletScreen = useMediaQuery('(max-width:1715px)');

  const FIXED_WIDTH = 800;
  const FIXED_HEIGHT = 500;
  const DESKTOP_WIDTH = 1200;
  const DESKTOP_HEIGHT = 720;

  const modalWidth = isTabletScreen ? FIXED_WIDTH : DESKTOP_WIDTH;
  const modalHeight = isTabletScreen ? FIXED_HEIGHT : DESKTOP_HEIGHT;

  useEffect(() => {
    let isMounted = true;

    const loadMedia = async () => {
      if (!selectedItem?.src) return;

      if (/^https?:\/\//.test(selectedItem.src)) {
        setMediaSrc(selectedItem.src);
        return;
      }

      setLoading(true);
      try {
        const { url } = await getDownloadFileUrl(selectedItem.src);
        if (isMounted) setMediaSrc(url);
      } catch (err) {
        void err;
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadMedia();

    return () => {
      isMounted = false;
      if (selectedItem?.src?.startsWith('blob:')) {
        URL.revokeObjectURL(selectedItem.src);
      }
    };
  }, [selectedItem]);

  if (loading) {
    return (
      <Modal
        open={showDialog}
        onClose={onClose}
        className="flex items-center justify-center"
      >
        <Box
          className="relative flex items-center justify-center p-4 rounded"
          sx={{
            width: modalWidth,
            height: modalHeight,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            outline: 'none'
          }}
        >
          Loading...
        </Box>
      </Modal>
    );
  }

  if (!mediaSrc) return null;

  return (
    <Modal
      open={showDialog}
      onClose={onClose}
      className="flex items-center justify-center"
    >
      <Box
        className="relative flex items-center justify-center p-4 rounded"
        sx={{
          width: modalWidth,
          height: modalHeight,
          backgroundColor: 'rgba(0,0,0,0.2)',
          outline: 'none',
          '&:focus': { outline: 'none' }
        }}
        tabIndex={-1}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: 5,
            right: 5,
            zIndex: 10,
            outline: 'none',
            '&:focus': { outline: 'none' }
          }}
          onClick={onClose}
        >
          <CloseIconWhite />
        </IconButton>

        {type === FileUploadType.Video ? (
          <video
            src={mediaSrc}
            controls
            autoPlay
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 8
            }}
          />
        ) : type === FileUploadType.Audio ? (
          <audio controls autoPlay style={{ width: '100%', height: 50 }}>
            <source src={mediaSrc} type={`audio/${selectedItem?.format}`} />
          </audio>
        ) : (
          <div className="w-full h-full flex items-center justify-center rounded-[8px] overflow-hidden">
            <img
              src={mediaSrc}
              alt=""
              className="max-w-full max-h-full object-contain rounded-[2.5px]"
            />
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default MediaModal;
